import { Form, Select, Button, Table, DatePicker, Space, InputNumber, Input, Spin, App, Card, Row, Col, Typography, Alert } from 'antd';
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { PageTitle, StandardCard } from '../../common';
import { apiClient } from '../../../utils/api';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title } = Typography;

interface MovimientoInsumo {
  id: number;
  comprobante_id: number;
  insumo: number;
  insumo_descripcion?: string;
  cantidad: number;
  precio_unitario: number;
  tasaiva: number;
  subtotal: number;
  iva: number;
  iinterno: number;
  fecha_entrega: string;
}

interface InsumoData {
  id: number;
  descripcion: string;
  tasaiva: number;
}

interface PadronData {
  id: number;
  razon: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface FormaPago {
  fecha_vencimiento: string;
  importe: number;
}

interface Comprobante {
  observaciones: string;
}

const CrearOrdenCompra: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { getAccessTokenSilently, isLoading: isAuth0Loading, user } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const [movimientos, setMovimientos] = useState<MovimientoInsumo[]>([]);
  const [insumos, setInsumos] = useState<InsumoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [padronSeleccionado, setPadronSeleccionado] = useState<PadronData | null>(null);
  const [padrones, setPadrones] = useState<PadronData[]>([]);
  const [formasPago, setFormasPago] = useState<FormaPago[]>([]);
  const [formaPagoForm] = Form.useForm();
  const navigate = useNavigate();
  const [observacion, setObservacion] = useState<string>('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Cargando datos...');

  const fetchData = async () => {
    try {
      setLoadingMessage('Cargando insumos...');
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get<InsumoData[]>(
        `${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${empresa}`,
        { token }
      );
      setInsumos(response.data);
    } catch (error: any) {
      console.error('Error cargando datos:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al cargar datos iniciales';
      message.error(errorMsg);
    }
  };

  const fetchPadrones = async () => {
    try {
      setLoadingMessage('Cargando proveedores...');
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get<PadronData[]>(
        `${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`,
        { token }
      );
      setPadrones(response.data);
    } catch (error: any) {
      console.error('Error cargando padrones:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al cargar proveedores';
      message.error(errorMsg);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (!empresa || !sucursal || isAuth0Loading) return;
      
      if (isLoadingData) {
        try {
          await fetchData();
          await fetchPadrones();
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    loadInitialData();
  }, [empresa, sucursal, isAuth0Loading]);

  const columns: ColumnsType<MovimientoInsumo> = [
    { 
      title: 'Entrega', 
      dataIndex: 'fecha_entrega',
      width: 100,
      render: (text: string) => new Date(text).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
    },
    { 
      title: 'Insumo', 
      dataIndex: 'insumo_descripcion',
      width: 200,
      ellipsis: true
    },
    {
      title: 'IVA %',
      dataIndex: 'tasaiva',
      width: 70,
      align: 'right',
      render: (value: number) => `${value}%`
    },
    { 
      title: 'Cant.', 
      dataIndex: 'cantidad', 
      width: 80,
      align: 'right' 
    },
    { 
      title: '$ Unit.', 
      dataIndex: 'precio_unitario',
      width: 100,
      align: 'right',
      render: (value: number) => `$${value.toFixed(2)}` 
    },
    { 
      title: 'Subtotal',
      dataIndex: 'subtotal',
      width: 100,
      align: 'right',
      render: (value: number) => `$${value.toFixed(2)}`
    },
    {
      title: '',
      key: 'acciones',
      width: 80,
      render: (_: any, record: MovimientoInsumo) => (
        <Button 
          type="link" 
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record)}
          size="small"
          style={{ padding: '0 4px' }}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  const handleAddItem = (values: any) => {
    const insumoSeleccionado = insumos.find(i => i.id === values.insumo);
    const subtotal = values.cantidad * values.precio_unitario;
    
    console.log('Insumo seleccionado:', insumoSeleccionado);
    console.log('TASAIVA del insumo:', insumoSeleccionado?.tasaiva);

    if (!insumoSeleccionado) {
      message.error('No se encontró el insumo');
      return;
    }

    const newItem: MovimientoInsumo = {
      id: 0,
      comprobante_id: 0,
      insumo: values.insumo,
      fecha_entrega: dayjs(values.fecha_entrega).format(),
      cantidad: values.cantidad,
      precio_unitario: values.precio_unitario,
      tasaiva: insumoSeleccionado.tasaiva,
      subtotal,
      iva: 0,
      iinterno: 0,
      insumo_descripcion: insumoSeleccionado.descripcion
    };

    setMovimientos([...movimientos, newItem]);
    form.resetFields(['insumo', 'cantidad', 'precio_unitario', 'fecha_entrega']);
  };

  const handleRemoveItem = (record: MovimientoInsumo) => {
    setMovimientos(movimientos.filter(item => item !== record));
  };

  const handleAddFormaPago = (values: any) => {
    const newFormaPago: FormaPago = {
      fecha_vencimiento: dayjs(values.fecha_vencimiento).format(),
      importe: values.importe
    };
    setFormasPago([...formasPago, newFormaPago]);
    formaPagoForm.resetFields();
  };

  const handleRemoveFormaPago = (record: FormaPago) => {
    setFormasPago(formasPago.filter(item => item !== record));
  };

  const formaPagoColumns: ColumnsType<FormaPago> = [
    { 
      title: 'Fecha Venc.', 
      dataIndex: 'fecha_vencimiento',
      width: 120,
      render: (text: string) => new Date(text).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
    },
    { 
      title: 'Importe', 
      dataIndex: 'importe',
      width: 120,
      align: 'right',
      render: (value: number) => `$${value.toFixed(2)}` 
    },
    {
      title: '',
      key: 'acciones',
      width: 80,
      render: (_: any, record: FormaPago) => (
        <Button 
          type="link" 
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveFormaPago(record)}
          size="small"
          style={{ padding: '0 4px' }}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  const handleFinish = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/crear_ocompra/${empresa}/${sucursal}`;

      await apiClient.post(
        ruta,
        {
          observacion,
          padron: padronSeleccionado?.id,
          movimientos: movimientos.map(m => ({
            ...m,
            id_empresa: empresa,
            id_sucursal: sucursal,
            alta: 1,
            tipo: 'OCR'
          })),
          formas_pago: formasPago.map(fp => ({
            ...fp,
            id_empresa: empresa,
            id_sucursal: sucursal,
            alta: 1,
          }))
        },
        { token }
      );

      message.success('Orden de compra creada correctamente');
      
      // Limpiar todos los formularios y estados
      form.resetFields();
      formaPagoForm.resetFields();
      setMovimientos([]);
      setFormasPago([]);
      setPadronSeleccionado(null);
      setObservacion('');
      
      // Recargar los insumos
      await fetchData();
      
    } catch (error: any) {
      console.error('Error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error al guardar los datos';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const calcularTotalMovimientos = () => {
    return movimientos.reduce((sum, item) => sum + Number(item.subtotal), 0);
  };

  const calcularTotalFormasPago = () => {
    return formasPago.reduce((sum, item) => sum + Number(item.importe), 0);
  };

  // Agregar la función isValidForm
  const isValidForm = () => {
    // Verificar que haya al menos un movimiento
    if (movimientos.length === 0) return false;
    
    // Verificar que haya al menos una forma de pago
    if (formasPago.length === 0) return false;
    
    // Verificar que los totales coincidan
    const totalMovimientos = calcularTotalMovimientos();
    const totalFormasPago = calcularTotalFormasPago();
    
    return Math.abs(totalMovimientos - totalFormasPago) < 0.01;
  };

  // Agregar la función handleCancel
  const handleCancel = () => {
    form.resetFields();
    formaPagoForm.resetFields();
    setMovimientos([]);
    setFormasPago([]);
    setPadronSeleccionado(null);
    setObservacion('');
    //navigate('/listar_ocompra');  // O la ruta que corresponda para volver al listado
  };

  // Función para calcular la diferencia y determinar el mensaje y color
  const getDiferenciaInfo = () => {
    const totalMovimientos = calcularTotalMovimientos();
    const totalFormasPago = calcularTotalFormasPago();
    const diferencia = totalFormasPago - totalMovimientos;

    if (Math.abs(diferencia) < 0.01) {
      return {
        mensaje: 'Los totales coinciden',
        color: 'green'
      };
    } else if (diferencia < 0) {
      return {
        mensaje: `Falta importe en forma de pago: ${diferencia.toLocaleString('es-AR', {
          style: 'currency',
          currency: 'ARS'
        })}`,
        color: 'red'
      };
    } else {
      return {
        mensaje: `Sobra importe en forma de pago: ${diferencia.toLocaleString('es-AR', {
          style: 'currency',
          currency: 'ARS'
        })}`,
        color: 'blue'
      };
    }
  };

  return (
    <div style={{ padding: '0' }}>
      {isLoadingData ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: '50vh',
          gap: '20px'
        }}>
          <Spin size="large" />
          <div style={{ fontSize: '14px' }}>{loadingMessage}</div>
        </div>
      ) : (
        <>
          {/* Header compacto tipo comprobante */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '2px solid #d9d9d9'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>ORDEN DE COMPRA</h2>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/listar_ocompra')}
              size="small"
              style={{ height: '28px', fontSize: '12px' }}
            >
              Volver
            </Button>
          </div>

          {/* Formulario principal tipo comprobante */}
          <Form form={form} layout="vertical">
            {/* Proveedor compacto en una línea */}
            <div style={{ 
              marginBottom: '8px',
              padding: '8px',
              backgroundColor: '#fafafa',
              borderRadius: '4px',
              border: '1px solid #e8e8e8'
            }}>
              <Row gutter={[16, 8]} align="middle">
                <Col xs={24} sm={6}>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>Proveedor:</span>
                </Col>
                <Col xs={24} sm={18}>
                  <Form.Item 
                    name="padron" 
                    rules={[{ required: true, message: 'Seleccione un proveedor' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione proveedor"
                      optionFilterProp="children"
                      size="small"
                      onChange={(value) => {
                        const padron = padrones.find(p => p.id === value);
                        setPadronSeleccionado(padron || null);
                      }}
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {padrones.map(padron => (
                        <Option key={padron.id} value={padron.id}>
                          {padron.razon} 
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              {padronSeleccionado && (
                <Row gutter={[16, 4]} style={{ marginTop: '8px', fontSize: '11px' }}>
                  <Col span={6}><strong>Nombre:</strong> {padronSeleccionado.razon}</Col>
                  <Col span={6}><strong>Dirección:</strong> {padronSeleccionado.direccion}</Col>
                  <Col span={6}><strong>Localidad:</strong> {padronSeleccionado.localidad}</Col>
                  <Col span={6}><strong>Provincia:</strong> {padronSeleccionado.provincia}</Col>
                </Row>
              )}
            </div>

            {/* Movimientos - Formulario compacto */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ 
                padding: '6px 8px', 
                backgroundColor: '#f0f0f0', 
                fontWeight: 600, 
                fontSize: '12px',
                borderTop: '1px solid #d9d9d9',
                borderLeft: '1px solid #d9d9d9',
                borderRight: '1px solid #d9d9d9'
              }}>
                MOVIMIENTOS
              </div>
              <div style={{ 
                padding: '8px',
                border: '1px solid #d9d9d9',
                borderTop: 'none'
              }}>
                <div style={{ marginBottom: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Entrega:</span>
                    <DatePicker 
                      format="DD/MM/YYYY" 
                      size="small" 
                      style={{ width: '110px' }}
                      onChange={(date) => form.setFieldValue('fecha_entrega', date)}
                    />
                  </div>
                  <div style={{ width: '220px' }}>
                    <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Insumo:</span>
                    <Select
                      showSearch
                      placeholder="Seleccione"
                      optionFilterProp="children"
                      size="small"
                      onChange={(value) => form.setFieldValue('insumo', value)}
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {insumos.map(ins => (
                        <Option key={ins.id} value={ins.id}>{ins.descripcion}</Option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Cant:</span>
                    <InputNumber 
                      min={0} 
                      step={0.01} 
                      size="small" 
                      style={{ width: '80px' }}
                      onChange={(value) => form.setFieldValue('cantidad', value)}
                    />
                  </div>
                  <div>
                    <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>$ Unit:</span>
                    <InputNumber 
                      min={0} 
                      step={0.01} 
                      size="small"
                      style={{ width: '100px' }}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      onChange={(value) => form.setFieldValue('precio_unitario', value)}
                    />
                  </div>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    size="small" 
                    style={{ height: '24px', fontSize: '11px', padding: '0 8px' }}
                    onClick={() => {
                      const values = form.getFieldsValue(['fecha_entrega', 'insumo', 'cantidad', 'precio_unitario']);
                      if (values.fecha_entrega && values.insumo && values.cantidad && values.precio_unitario) {
                        handleAddItem(values);
                      } else {
                        message.warning('Complete todos los campos');
                      }
                    }}
                  >
                    +
                  </Button>
                </div>
                <Table
                  dataSource={movimientos}
                  columns={columns}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content', y: '120px' }}
                  style={{ fontSize: '11px' }}
                  summary={(pageData) => {
                    if (!pageData.length) return null;
                    const total = pageData.reduce((sum, item) => sum + item.subtotal, 0);
                    return (
                      <Table.Summary fixed="bottom">
                        <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold', fontSize: '11px' }}>
                          <Table.Summary.Cell index={0} colSpan={5} align="right">
                            <strong>TOTAL:</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <strong>${total.toFixed(2)}</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} />
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            </div>

            {/* Forma de Pago y Observaciones lado a lado */}
            <Row gutter={[8, 8]} style={{ marginBottom: '8px', alignItems: 'stretch' }}>
              <Col xs={24} md={14}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: '100%'
                }}>
                  <div style={{ 
                    padding: '6px 8px', 
                    backgroundColor: '#f0f0f0', 
                    fontWeight: 600, 
                    fontSize: '12px',
                    borderTop: '1px solid #d9d9d9',
                    borderLeft: '1px solid #d9d9d9',
                    borderRight: '1px solid #d9d9d9'
                  }}>
                    FORMA DE PAGO
                  </div>
                  <div style={{ 
                    padding: '8px',
                    border: '1px solid #d9d9d9',
                    borderTop: 'none',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div>
                        <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Vencimiento:</span>
                        <DatePicker 
                          format="DD/MM/YYYY" 
                          size="small" 
                          style={{ width: '110px' }}
                          onChange={(date) => formaPagoForm.setFieldValue('fecha_vencimiento', date)}
                        />
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Importe:</span>
                        <InputNumber
                          size="small"
                          style={{ width: '100px' }}
                          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          min={0}
                          step={0.01}
                          onChange={(value) => formaPagoForm.setFieldValue('importe', value)}
                        />
                      </div>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        size="small" 
                        style={{ height: '24px', fontSize: '11px', padding: '0 8px' }}
                        onClick={() => {
                          const values = formaPagoForm.getFieldsValue(['fecha_vencimiento', 'importe']);
                          if (values.fecha_vencimiento && values.importe) {
                            handleAddFormaPago(values);
                          } else {
                            message.warning('Complete todos los campos');
                          }
                        }}
                      >
                        +
                      </Button>
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <Table
                        dataSource={formasPago}
                        columns={formaPagoColumns}
                        pagination={false}
                        size="small"
                        scroll={{ x: 'max-content', y: '80px' }}
                        style={{ fontSize: '11px' }}
                        summary={(pageData) => {
                          if (!pageData.length) return null;
                          const total = pageData.reduce((sum, item) => sum + (item.importe || 0), 0);
                          return (
                            <Table.Summary fixed="bottom">
                              <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold', fontSize: '11px' }}>
                                <Table.Summary.Cell index={0} colSpan={1} align="right">
                                  <strong>TOTAL:</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                  <strong>${total.toFixed(2)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} />
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={10}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: '100%',
                  gap: '8px'
                }}>
                  <div style={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ 
                      padding: '6px 8px', 
                      backgroundColor: '#f0f0f0', 
                      fontWeight: 600, 
                      fontSize: '12px',
                      borderTop: '1px solid #d9d9d9',
                      borderLeft: '1px solid #d9d9d9',
                      borderRight: '1px solid #d9d9d9'
                    }}>
                      OBSERVACIONES
                    </div>
                    <div style={{ 
                      padding: '8px',
                      border: '1px solid #d9d9d9',
                      borderTop: 'none',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Input.TextArea 
                        rows={3}
                        placeholder="Opcional"
                        style={{ fontSize: '11px', flex: 1 }}
                        value={observacion}
                        onChange={(e) => setObservacion(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Resumen y Acciones debajo de Observaciones */}
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: '#fafafa',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    flexShrink: 0
                  }}>
                    <Alert
                      message={<span style={{ fontSize: '11px' }}>{getDiferenciaInfo().mensaje}</span>}
                      type={
                        Math.abs(calcularTotalFormasPago() - calcularTotalMovimientos()) < 0.01 
                          ? 'success' 
                          : calcularTotalFormasPago() < calcularTotalMovimientos() 
                            ? 'error' 
                            : 'info'
                      }
                      style={{ fontSize: '11px', marginBottom: '8px' }}
                      showIcon
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                      <Button onClick={handleCancel} size="small" style={{ height: '28px', fontSize: '12px' }}>
                        Cancelar
                      </Button>
                      <Button 
                        type="primary" 
                        onClick={handleFinish}
                        disabled={!isValidForm()}
                        loading={loading}
                        size="small"
                        style={{ height: '28px', fontSize: '12px' }}
                      >
                        Crear Orden
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

export default CrearOrdenCompra;